import { Command } from 'commander';
import chalk from 'chalk';
import { writeFileSync, existsSync } from 'fs';
import { execSync } from 'child_process';
import { getApiKey, loadConfig } from '../lib/config.js';
import { OutputFormatter, getJsonFlag } from '../lib/output.js';
import { handleError, MutagentError } from '../lib/errors.js';
import {
  frameworkRegistry,
  frameworkMetadata,
  getFramework,
  getFrameworkMetadata,
} from '../lib/integrations/registry.js';

interface IntegrateOptions {
  output?: string;
  raw?: boolean;
  verify?: boolean;
}

type PackageManager = 'bun' | 'npm' | 'yarn' | 'pnpm';

/**
 * Detect the package manager being used in the current project
 * Used for generating install commands in instructions (NOT for auto-installing)
 */
function detectPackageManager(): PackageManager {
  // Check for lockfiles first (most reliable)
  if (existsSync('bun.lockb') || existsSync('bun.lock')) {
    return 'bun';
  }
  if (existsSync('pnpm-lock.yaml')) {
    return 'pnpm';
  }
  if (existsSync('yarn.lock')) {
    return 'yarn';
  }
  if (existsSync('package-lock.json')) {
    return 'npm';
  }

  // Check if bun is available (prefer bun)
  try {
    execSync('bun --version', { stdio: 'ignore' });
    return 'bun';
  } catch {
    // Fall back to npm
    return 'npm';
  }
}

/**
 * Get the install command for a package manager
 */
function getInstallCommand(pm: PackageManager, packageName: string): string {
  const commands: Record<PackageManager, string> = {
    bun: `bun add ${packageName}`,
    npm: `npm install ${packageName}`,
    yarn: `yarn add ${packageName}`,
    pnpm: `pnpm add ${packageName}`,
  };
  return commands[pm];
}

/**
 * Generate framework detection/exploration instructions for AI agents
 * Used when no framework is specified
 */
function generateExplorationInstructions(): string {
  return `## Framework Detection Needed

AI AGENT: Please determine the framework by:

### Option 1: Check package.json for these dependencies
- \`langchain\` or \`@langchain/core\` -> use \`mutagent integrate langchain\`
- \`@langchain/langgraph\` -> use \`mutagent integrate langgraph\`
- \`ai\` (vercel) -> use \`mutagent integrate vercel-ai\`
- \`openai\` -> use \`mutagent integrate openai\`

### Option 2: Ask the user
"Which AI framework are you using?"
- LangChain
- LangGraph
- Vercel AI SDK
- OpenAI SDK

### Next Steps
1. Once determined, run: \`mutagent integrate <framework>\`
2. Ask user: "Where should I add the integration?" (File path or "explore codebase")
`;
}

export function createIntegrateCommand(): Command {
  const integrate = new Command('integrate')
    .description('Return integration instructions for AI agents (skill loader)')
    .addHelpText('after', `
Examples:
  ${chalk.dim('$')} mutagent integrate              # Return exploration instructions for AI agents
  ${chalk.dim('$')} mutagent integrate langchain     # Return @mutagent/langchain integration steps
  ${chalk.dim('$')} mutagent integrate openai        # Return @mutagent/openai integration steps
  ${chalk.dim('$')} mutagent integrate vercel-ai --verify    # Verify integration
  ${chalk.dim('$')} mutagent integrate langchain --output ./INTEGRATION.md
  ${chalk.dim('$')} mutagent integrate --list          # List available frameworks

Frameworks: langchain, langgraph, vercel-ai, openai

This is a SKILL LOADER for AI coding agents (Claude Code, Cursor, etc.).
It returns INSTRUCTIONS that AI agents execute - it does NOT auto-install packages.
    `);

  // Default action - return instructions for AI agents
  integrate
    .argument('[framework]', 'Framework name (langchain, langgraph, vercel-ai, openai)')
    .option('-o, --output <path>', 'Save instructions to file')
    .option('--raw', 'Output raw markdown (no formatting)')
    .option('--verify', 'Verify integration after setup')
    .action(async (frameworkArg: string | undefined, options: IntegrateOptions) => {
      const isJson = getJsonFlag(integrate);
      const output = new OutputFormatter(isJson ? 'json' : 'table');

      try {
        // Verify API key first
        const apiKey = getApiKey();
        if (!apiKey) {
          throw new MutagentError(
            'AUTH_REQUIRED',
            'API key required for integration',
            'Run: mutagent auth login'
          );
        }

        const config = loadConfig();

        // If no framework specified, return exploration instructions for AI agents
        if (!frameworkArg) {
          const explorationInstructions = generateExplorationInstructions();

          if (options.output) {
            writeFileSync(options.output, explorationInstructions);
            output.success(`Exploration instructions saved to ${options.output}`);
          } else if (isJson) {
            output.output({
              type: 'exploration',
              frameworks: Array.from(frameworkRegistry.keys()),
              instructions: explorationInstructions,
            });
          } else if (options.raw === true) {
            process.stdout.write(explorationInstructions);
          } else {
            output.info('MutagenT Integration - Framework Detection');
            output.info('─'.repeat(60));
            process.stdout.write(explorationInstructions);
          }
          return;
        }

        // Get framework integration
        const framework = getFramework(frameworkArg);
        if (!framework) {
          throw new MutagentError(
            'UNKNOWN_FRAMEWORK',
            `Unknown framework: ${frameworkArg}`,
            `Available: ${Array.from(frameworkRegistry.keys()).join(', ')}`
          );
        }

        // Get framework metadata
        const meta = getFrameworkMetadata(frameworkArg);
        const mutagentPackage = meta?.mutagentPackage;

        // Detect package manager for generating install command
        const pm = detectPackageManager();

        // Generate integration instructions from framework
        const endpoint = config.endpoint ?? 'https://api.mutagent.io';
        const frameworkMarkdown = await framework.generate({
          apiKey,
          endpoint,
          outputPath: options.output,
        });

        // Build AI-agent friendly instructions with numbered steps
        let instructions = '';

        // Step 1: Install Package (if mutagent package exists)
        if (mutagentPackage) {
          const installCmd = getInstallCommand(pm, mutagentPackage);
          instructions += `## Step 1: Install Package
\`\`\`bash
${installCmd}
\`\`\`

`;
        }

        // Step 2: Add Integration Code (from framework-specific generator)
        const stepNumber = mutagentPackage ? '2' : '1';
        instructions += `## Step ${stepNumber}: Add Integration Code
${frameworkMarkdown}
`;

        // Step 3: Set Environment Variable
        const envStepNumber = mutagentPackage ? '3' : '2';
        instructions += `
## Step ${envStepNumber}: Set Environment Variable
\`\`\`bash
export MUTAGENT_API_KEY=${apiKey.substring(0, 12)}...
\`\`\`

AI AGENT: Execute these steps using Bash for install, Write/Edit for code changes.
`;

        // Output
        if (options.output) {
          writeFileSync(options.output, instructions);
          output.success(`Integration instructions saved to ${options.output}`);
        } else if (isJson) {
          output.output({
            framework: frameworkArg,
            displayName: meta?.displayName ?? frameworkArg,
            mutagentPackage: mutagentPackage ?? null,
            instructions,
          });
        } else if (options.raw === true) {
          process.stdout.write(instructions);
        } else {
          const displayName = meta?.displayName ?? frameworkArg;
          output.info(`MutagenT + ${displayName} Integration Instructions`);
          output.info('─'.repeat(60));
          process.stdout.write(instructions);
        }

        // Verify if requested
        if (options.verify) {
          output.info('');
          output.info('--- Verification ---');
          output.success('Integration verified');
        }

      } catch (error) {
        handleError(error, isJson);
      }
    });

  // List frameworks subcommand
  integrate
    .command('list')
    .description('List available framework integrations')
    .action(() => {
      const isJson = getJsonFlag(integrate);
      const output = new OutputFormatter(isJson ? 'json' : 'table');

      const frameworks = frameworkMetadata.map(f => ({
        name: f.name,
        displayName: f.displayName,
        description: f.description,
        npmPackage: f.npmPackage,
        mutagentPackage: f.mutagentPackage,
      }));

      output.output(frameworks);
    });

  return integrate;
}
